import { isDarkColor } from "@/pages/builders/app/appThemePalette";

/**
 * Canonical design-direction registry — the SINGLE source of truth for the
 * palette + type identity of every "look" a customer can pick, shared across
 * all five builders (App, Website, Email, Doc, Wallet). Pure module, no React.
 *
 * WHY: the five galleries were built independently and drifted — the same
 * "gala" rendered four different golds, "networking" split into five unrelated
 * palettes. Each builder now sources its palette (and, where it can, its fonts)
 * from a direction here, so one look reads the same on every surface a guest
 * touches. See attendee-app-themes-2026/DESIGN_DIRECTIONS_SYNC_PLAN.md for the
 * design rationale and the per-conflict "which variant wins" decisions.
 *
 * SCOPE: colors (the 4-tuple) + rounding apply universally. fontHead/fontBody
 * are the direction's INTENDED typography — consumed by surfaces that can load
 * the face (Website/Email/Doc). The App keeps its own Google-loadable family
 * per design (a licensed face like Didot/Neue Haas can't be fetched on-device),
 * so the App syncs colors + rounding only. This registry is NOT a layout — each
 * builder keeps its own structure; only the direction's tokens are centralized.
 *
 * Adding/retuning a direction is a one-line edit here; the pinned fixture
 * (directions.fixture.json) guards against an accidental hex change slipping
 * through — regenerate it in the same change (UPDATE_DIRECTIONS_FIXTURE=1).
 */

export type DirectionId =
  // 16 base
  | "webinar"
  | "speaker"
  | "gala"
  | "charity"
  | "dinner"
  | "press"
  | "product-launch"
  | "corporate"
  | "workshop"
  | "minimal"
  | "summit"
  | "soiree"
  | "networking-night"
  | "networking"
  | "festival"
  | "sports"
  // + community (Hearth) + keynote (Aurora Launch + Keynote Dark consolidated)
  | "community"
  | "keynote"
  // 6 premium
  | "aurora"
  | "noir"
  | "meridian"
  | "spectra"
  | "lumen"
  | "verdant";

export type DirectionRounding = "none" | "sm" | "md" | "lg" | "xl";

export type DesignDirection = {
  id: DirectionId;
  /** i18n key for the display name (namespace reserved; not wired to galleries
   *  in the sync pass — surfaces keep their own branded names for now). */
  nameKey: string;
  primary: string; // hex6
  accent: string; // hex6
  background: string; // hex6
  text: string; // hex6
  /** Display/heading family name (bare — consumers append fallbacks). */
  fontHead: string;
  /** Body family name (bare). */
  fontBody: string;
  rounding: DirectionRounding;
  /** Polarity of `background` (Flutter estimateBrightnessForColor rule, shared
   *  with the in-phone palette). Derived, not authored. */
  isDark: boolean;
};

/** Authored fields (everything except the derived `isDark` and the id-derived
 *  `nameKey`). Order top→bottom: primary, accent, background, text, head, body,
 *  rounding — matching the canon table in DESIGN_DIRECTIONS_SYNC_PLAN.md §2. */
type Seed = Omit<DesignDirection, "id" | "nameKey" | "isDark">;

const SEEDS: Record<DirectionId, Seed> = {
  // ── 16 base ──────────────────────────────────────────────────────────────
  webinar: { primary: "#1E40AF", accent: "#475569", background: "#F7F2ED", text: "#0F172A", fontHead: "Georgia", fontBody: "Inter", rounding: "md" },
  speaker: { primary: "#CC1F1F", accent: "#2563EB", background: "#FFFBEB", text: "#1A1A1A", fontHead: "Futura", fontBody: "Helvetica Neue", rounding: "none" },
  gala: { primary: "#C9A24B", accent: "#F4E7B8", background: "#0A0A0A", text: "#F4F1EA", fontHead: "Didot", fontBody: "Inter", rounding: "sm" },
  charity: { primary: "#3D5A3D", accent: "#C4D5B8", background: "#FEFCF8", text: "#1A1A1A", fontHead: "Cormorant Garamond", fontBody: "Inter", rounding: "lg" },
  dinner: { primary: "#7B1E2B", accent: "#C9A96E", background: "#FFFCF7", text: "#2C2C2C", fontHead: "Playfair Display", fontBody: "Inter", rounding: "md" },
  press: { primary: "#C00000", accent: "#1A1A1A", background: "#FFFCF7", text: "#1A1A1A", fontHead: "Playfair Display", fontBody: "Georgia", rounding: "none" },
  "product-launch": { primary: "#C0C5CC", accent: "#8B95A3", background: "#0A0A0A", text: "#FFFFFF", fontHead: "Neue Haas Grotesk", fontBody: "Inter", rounding: "md" },
  corporate: { primary: "#7A602E", accent: "#C9A96E", background: "#F5F0E8", text: "#2A2419", fontHead: "Bodoni 72", fontBody: "Adobe Caslon Pro", rounding: "sm" },
  workshop: { primary: "#C89560", accent: "#896642", background: "#FAFAF7", text: "#1A1A1A", fontHead: "Inter", fontBody: "Söhne", rounding: "lg" },
  minimal: { primary: "#111111", accent: "#6B6B6B", background: "#FFFFFF", text: "#111111", fontHead: "Work Sans", fontBody: "Inter", rounding: "none" },
  summit: { primary: "#0F5E59", accent: "#161412", background: "#FAF7F2", text: "#161412", fontHead: "Fraunces", fontBody: "Hanken Grotesk", rounding: "md" },
  soiree: { primary: "#D9B25F", accent: "#F3E3BC", background: "#100A18", text: "#FFFFFF", fontHead: "Playfair Display", fontBody: "Hanken Grotesk", rounding: "md" },
  "networking-night": { primary: "#312F81", accent: "#4F46E5", background: "#FBFAF7", text: "#161616", fontHead: "Bricolage Grotesque", fontBody: "Hanken Grotesk", rounding: "lg" },
  networking: { primary: "#0369A1", accent: "#6366F1", background: "#F0F9FF", text: "#0F172A", fontHead: "Poppins", fontBody: "Poppins", rounding: "md" },
  festival: { primary: "#DB2266", accent: "#FFD23F", background: "#100A1A", text: "#F7F0FF", fontHead: "Poppins", fontBody: "Poppins", rounding: "xl" },
  sports: { primary: "#132A4A", accent: "#C8FF3D", background: "#0B1522", text: "#EAF1F8", fontHead: "Montserrat", fontBody: "Montserrat", rounding: "md" },
  // ── community + keynote ────────────────────────────────────────────────────
  community: { primary: "#B85434", accent: "#2E7D6B", background: "#FBF4EC", text: "#2A231E", fontHead: "Figtree", fontBody: "Figtree", rounding: "xl" },
  keynote: { primary: "#3B5BFF", accent: "#4DE1C1", background: "#0B0E17", text: "#F4F6FB", fontHead: "Montserrat", fontBody: "Montserrat", rounding: "lg" },
  // ── 6 premium ──────────────────────────────────────────────────────────────
  aurora: { primary: "#C9A24B", accent: "#F4F1EA", background: "#0C0C0E", text: "#F4F1EA", fontHead: "Cormorant Garamond", fontBody: "Neue Haas Grotesk", rounding: "sm" },
  noir: { primary: "#7A6A52", accent: "#C9A96E", background: "#F7F3EC", text: "#1A1714", fontHead: "Cormorant Garamond", fontBody: "Neue Haas Grotesk", rounding: "none" },
  meridian: { primary: "#15233B", accent: "#1F6F8B", background: "#FFFFFF", text: "#15233B", fontHead: "Neue Haas Grotesk", fontBody: "Neue Haas Grotesk", rounding: "sm" },
  spectra: { primary: "#22D3EE", accent: "#FF5C5C", background: "#0E0E12", text: "#FFFFFF", fontHead: "Neue Haas Grotesk", fontBody: "Neue Haas Grotesk", rounding: "md" },
  lumen: { primary: "#4F46E5", accent: "#4F46E5", background: "#FAFAFA", text: "#0A0A0A", fontHead: "Neue Haas Grotesk", fontBody: "Neue Haas Grotesk", rounding: "md" },
  verdant: { primary: "#1F3D2B", accent: "#8F5330", background: "#F4F1EA", text: "#1F3D2B", fontHead: "Georgia", fontBody: "Georgia", rounding: "sm" },
};

/** Stable id order for iteration + fixture serialization. */
export const DIRECTION_IDS = Object.keys(SEEDS) as DirectionId[];

function build(): Record<DirectionId, DesignDirection> {
  const out = {} as Record<DirectionId, DesignDirection>;
  for (const id of DIRECTION_IDS) {
    const s = SEEDS[id];
    out[id] = { id, nameKey: `designDirections.${id}`, ...s, isDark: isDarkColor(s.background) };
  }
  return out;
}

/** The full canonical registry, keyed by id. */
export const DIRECTIONS: Record<DirectionId, DesignDirection> = build();

/** Lookup with a clear throw on an unknown id (calling code passes a literal
 *  DirectionId, so this only fires on a bad cast / stale data). */
export function direction(id: DirectionId): DesignDirection {
  const d = DIRECTIONS[id];
  if (!d) throw new Error(`Unknown design direction: ${id}`);
  return d;
}

/**
 * CSS family → the real name of the woff2 the WEBSITE BUILDER serves under it
 * (`WB_FONT_FILES` in `wbFontPairings.ts`): jost-*.woff2 under 'Futura',
 * gelasio-* under 'Georgia', bodoni-moda under 'Didot', archivo under
 * 'Neue Haas Grotesk'.
 *
 * The CSS name stays — every stored `fonts.header` / `fontType` carries it and
 * a rename would orphan them. What a picker SHOWS must nevertheless be the
 * face the user gets (Kanzlei 05.10., 7.13), and that depends on the SURFACE
 * (audit R1 of the same day, K2-09), so this map is NOT a universal label:
 *
 *  - a CURATED website pairing ships the `wb-fonts` @font-face block, so the
 *    published site really paints these files → these names
 *    (`wbFontPairings.ts`, only for a family the pairing ships a face for);
 *  - a SYSTEM website pair or an off-catalogue stack serves nothing — the
 *    visitor's OS decides → the stack's own family name (`FontBody.tsx`);
 *  - a document PDF embeds what `DocumentPdfExporter.registerFonts` registers,
 *    which is a different set (Didot → Playfair Display, Neue Haas Grotesk →
 *    Inter) → `pdfEmbeddedFace` in `directionTheme.ts`, used by the DOCUMENT
 *    surface of the A4 picker (`A4Builder/settings/font-options.document.ts`);
 *  - an e-mail is rendered by the recipient's mail client: the e-mail builder
 *    shows the catalogue's own names (`A4Builder/settings/font-options.ts`).
 *
 * Only families with a foreign file under their name are listed; everything
 * else is shown under its own name. Pinned against `WB_FONT_FILES` in
 * `wbFontPairings.test.ts`.
 */
export const FONT_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  Futura: "Jost",
  Georgia: "Gelasio",
  Didot: "Bodoni Moda",
  "Neue Haas Grotesk": "Archivo",
};

/** The bundled face the website builder serves under a CSS family name, or the
 *  family itself where it serves no foreign file. Only true for a tile whose
 *  @font-face is actually shipped — see {@link FONT_DISPLAY_NAMES}. Never feed
 *  the result back into CSS or a stored template; it is display text only. */
export function fontDisplayName(family: string): string {
  // Own keys only: a family this map does not know — a newly hosted one, or a
  // name like "constructor" that a plain lookup answers from Object.prototype
  // — is shown under its own name.
  return Object.hasOwn(FONT_DISPLAY_NAMES, family) ? (FONT_DISPLAY_NAMES[family] ?? family) : family;
}
